import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  LoadingState,
} from '@cca/ui';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';

interface Connection {
  readonly id: string;
  readonly provider: string;
  /** Which store this credential belongs to; an org can connect several. */
  readonly shopDomain: string | null;
  readonly storeId: string | null;
  readonly status: string;
  readonly grantedScopes: readonly string[];
  readonly requiredScopes: readonly string[];
  readonly isSimulated: boolean;
  readonly lastHealthCheckAt: string | null;
  readonly lastHealthStatus: string | null;
  readonly lastErrorCode: string | null;
  readonly lastErrorAt: string | null;
  readonly disconnectedAt: string | null;
}

interface ConnectionsResponse {
  readonly simulatorEnabled: boolean;
  readonly requiredShopifyScopes: readonly string[];
  readonly shopifyConfigured: boolean;
  readonly connections: readonly Connection[];
}

interface HealthResult {
  readonly ok: boolean;
  readonly status: string;
  readonly simulated: boolean;
  readonly grantedScopes: readonly string[];
  readonly missingScopes: readonly string[];
  readonly message: string;
  readonly checkedAt: string;
}

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  CONNECTED: 'success',
  SIMULATED: 'info',
  PENDING: 'warning',
  MISSING_SCOPE: 'warning',
  ERROR: 'danger',
  DISCONNECTED: 'neutral',
};

/**
 * Shopify connection.
 *
 * Every control here is wired to a real endpoint. The connect button is
 * disabled with a stated reason rather than hidden when the environment cannot
 * complete an install — a button that silently does nothing is worse than one
 * that explains why it cannot.
 */
export function ShopifyConnection() {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [shop, setShop] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthResult | null>(null);

  // A Shopify-initiated install lands on /settings?connect_shop=<domain>, with
  // the domain already verified against Shopify's HMAC by the worker. The
  // merchant never types it.
  const invitedShop =
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search).get('connect_shop');
  const autoStarted = useRef(false);

  const connections = useQuery({
    queryKey: ['connections'],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<ConnectionsResponse>('/api/connections', { accessToken }),
  });

  const authorize = useMutation({
    mutationFn: (input: { shop: string }) =>
      apiRequest<{ authorizeUrl: string }>('/api/connections/shopify/authorize', {
        method: 'POST',
        accessToken,
        body: input,
      }),
    onSuccess: (result) => {
      // Full navigation, not a fetch: this is the merchant's own OAuth consent
      // on Shopify's domain, and it must happen in their browser.
      window.location.assign(result.authorizeUrl);
    },
    onError: (error) => {
      setNotice(error instanceof ApiError ? error.message : 'Could not start the install.');
    },
  });

  const checkHealth = useMutation({
    mutationFn: () =>
      apiRequest<HealthResult>('/api/connections/shopify/health', {
        method: 'POST',
        accessToken,
      }),
    onSuccess: (result) => {
      setHealth(result);
      setNotice(null);
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
    onError: (error) => {
      setNotice(error instanceof ApiError ? error.message : 'The health check failed.');
    },
  });

  const disconnect = useMutation({
    mutationFn: (input: { storeId: string }) =>
      apiRequest<{ status: string }>('/api/connections/shopify/disconnect', {
        method: 'POST',
        accessToken,
        body: input,
      }),
    onSuccess: () => {
      setHealth(null);
      setNotice('Disconnected. The stored credential was destroyed; existing orders are kept.');
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
    onError: (error) => {
      setNotice(error instanceof ApiError ? error.message : 'Could not disconnect.');
    },
  });

  // Start the grant as soon as a session exists, once. The guard matters: this
  // navigates away, and without it a re-render mid-redirect would mint a second
  // install nonce and leave the first one dangling.
  useEffect(() => {
    if (!invitedShop || autoStarted.current) return;
    if (!accessToken || !connections.data) return;
    if (connections.data.simulatorEnabled || !connections.data.shopifyConfigured) return;
    autoStarted.current = true;
    authorize.mutate({ shop: invitedShop });
  }, [invitedShop, accessToken, connections.data, authorize]);

  if (connections.isPending) return <LoadingState label="Loading connections" />;
  if (connections.isError) {
    return (
      <ErrorState
        description={
          connections.error instanceof ApiError
            ? connections.error.message
            : 'Could not load connections.'
        }
        onRetry={() => void connections.refetch()}
      />
    );
  }

  const data = connections.data;
  const shopify = data.connections.find((connection) => connection.provider === 'shopify');
  const canInstall = data.shopifyConfigured && !data.simulatorEnabled;

  const blockedReason = data.simulatorEnabled
    ? 'This runtime uses the development simulator. Disable DEVELOPMENT_SIMULATOR_ENABLED before connecting a real store, so a simulated result can never be mistaken for a real one.'
    : !data.shopifyConfigured
      ? 'SHOPIFY_API_KEY and SHOPIFY_API_SECRET are not set on the worker.'
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shopify</CardTitle>
        <CardDescription>
          Customers, orders, fulfilments and tracking. The access token is stored encrypted and is
          never returned to this page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {shopify ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONE[shopify.status] ?? 'neutral'}>
                {shopify.status.toLowerCase().replaceAll('_', ' ')}
              </Badge>
              {shopify.shopDomain ? (
                <span className="font-mono text-xs text-slate-600 dark:text-slate-400">
                  {shopify.shopDomain}
                </span>
              ) : null}
              {shopify.isSimulated ? <Badge tone="warning">simulated</Badge> : null}
              {shopify.lastHealthCheckAt ? (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  checked {new Date(shopify.lastHealthCheckAt).toLocaleString()}
                </span>
              ) : null}
            </div>

            {shopify.lastErrorCode ? (
              <p className="text-xs text-rose-700 dark:text-rose-400">
                Last error: {shopify.lastErrorCode}
                {shopify.lastErrorAt ? ` at ${new Date(shopify.lastErrorAt).toLocaleString()}` : ''}
              </p>
            ) : null}

            <dl className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Granted scopes</dt>
                <dd className="font-mono text-slate-700 dark:text-slate-300">
                  {shopify.grantedScopes.length > 0 ? shopify.grantedScopes.join(', ') : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Required scopes</dt>
                <dd className="font-mono text-slate-700 dark:text-slate-300">
                  {data.requiredShopifyScopes.length > 0
                    ? data.requiredShopifyScopes.join(', ')
                    : '—'}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                disabled={checkHealth.isPending}
                onClick={() => checkHealth.mutate()}
              >
                {checkHealth.isPending ? 'Checking…' : 'Check connection'}
              </Button>
              {shopify.status !== 'DISCONNECTED' && shopify.storeId ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disconnect.isPending}
                  onClick={() => disconnect.mutate({ storeId: shopify.storeId! })}
                >
                  Disconnect
                </Button>
              ) : null}
            </div>

            {health ? (
              <p
                role="status"
                className={`rounded-md px-3 py-2 text-xs ${
                  health.ok
                    ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200'
                    : 'bg-rose-50 text-rose-900 dark:bg-rose-950 dark:text-rose-200'
                }`}
              >
                {health.simulated ? 'Simulated check. ' : ''}
                {health.message}
                {health.missingScopes.length > 0
                  ? ` Missing: ${health.missingScopes.join(', ')}.`
                  : ''}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            No Shopify store is connected to this organisation.
          </p>
        )}

        {invitedShop ? (
          <p
            role="status"
            className="rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:bg-sky-950 dark:text-sky-200"
          >
            Continuing the install for <span className="font-mono">{invitedShop}</span>, sent by
            Shopify. Taking you there to approve the app…
          </p>
        ) : null}

        <form
          className="flex flex-wrap items-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800"
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault();
            const trimmed = shop.trim();
            if (trimmed) authorize.mutate({ shop: trimmed });
          }}
        >
          <label className="flex-1 text-sm">
            <span className="mb-1 block text-slate-700 dark:text-slate-300">
              {shopify ? 'Reconnect or connect another store' : 'Connect a store'}
            </span>
            <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
              Installing from Shopify instead? You will not need to type this — Shopify tells us
              which store, and signs it.
            </span>
            <Input
              value={shop}
              onChange={(changeEvent) => setShop(changeEvent.target.value)}
              placeholder="acme-store.myshopify.com"
              disabled={!canInstall}
              autoComplete="off"
            />
          </label>
          <Button type="submit" size="sm" disabled={!canInstall || authorize.isPending}>
            {authorize.isPending ? 'Starting…' : 'Connect Shopify'}
          </Button>
        </form>

        {blockedReason ? (
          <p className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
            {blockedReason}
          </p>
        ) : (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            You will be sent to Shopify to approve the app. The install link is valid for ten
            minutes and can be used once.
          </p>
        )}

        {notice ? (
          <p role="status" className="text-xs text-slate-700 dark:text-slate-300">
            {notice}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
